import { useEffect } from 'react';

import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Field, FieldProps, Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import {
  AiCallInputPayload,
  useAiCallPayloadLazyQuery,
  useCallOutWithAiMutation,
} from '../../gql/generated/graphql';

import Modal from '../shared/Modal';

type AiCallModalProps = {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
  calledNumber: string;
};

// Validation schema
const validationSchema = Yup.object({
  dealId: Yup.string().required('Deal ID is required'),
  dealSource: Yup.string().required('Deal Source is required'),
  driverName: Yup.string().required('Driver Name is required'),
  repName: Yup.string().required('Rep Name is required'),
  vehicleYearMakeModel: Yup.string().required('Vehicle Year/Make/Model is required'),
  vehicleMileage: Yup.string().required('Vehicle Mileage is required'),
  payoffAmount: Yup.string().required('Payoff Amount is required'),
  bankName: Yup.string().required('Bank Name is required'),
  termInMonths: Yup.string().required('Term in Months is required'),
  sellRate: Yup.string().required('Sell Rate is required'),
  buyRate: Yup.string().required('Buy Rate is required'),
  monthlyPaymentWithVSCGAP: Yup.string().required('Monthly Payment (VSC+GAP) is required'),
  monthlyPaymentWithVSC: Yup.string().required('Monthly Payment (VSC Only) is required'),
  monthlyPaymentWithGap: Yup.string().required('Monthly Payment (GAP Only) is required'),
  monthlyPaymentWithNoCoverage: Yup.string().required('Monthly Payment (No Coverage) is required'),
  firstPaymentDate: Yup.string().required('First Payment Date is required'),
  vscYears: Yup.string().required('VSC Years is required'),
  vscMiles: Yup.string().required('VSC Miles is required'),
  currentMiles__vscMiles: Yup.string().required('Current Miles / VSC Miles is required'),
  originalWarrantyYears: Yup.string().required('Original Warranty Years is required'),
  originalWarrantyMiles: Yup.string().required('Original Warranty Miles is required'),
  lienholderName: Yup.string().required('Lienholder Name is required'),
  maxMarkup: Yup.string().required('Max Markup is required'),
  moneyDown: Yup.string().required('Money Down is required'),
  // Boolean fields are optional - can be 'true', 'false', or empty
  isBuyerNotLessee: Yup.string(),
  isTitleOnly: Yup.string(),
  isLesseeOnlyOrDoubleTax: Yup.string(),
  isWalkInRequired: Yup.string(),
  isDirectPayDeal: Yup.string(),
  isRegTransferEligible: Yup.string(),
});

// Initial form values
const initialValues: AiCallInputPayload = {
  dealId: '',
  dealSource: '',
  driverName: '',
  repName: '',
  vehicleYearMakeModel: '',
  vehicleMileage: '',
  payoffAmount: '',
  bankName: '',
  termInMonths: '',
  sellRate: '',
  buyRate: '',
  monthlyPaymentWithVSCGAP: '',
  monthlyPaymentWithVSC: '',
  monthlyPaymentWithGap: '',
  monthlyPaymentWithNoCoverage: '',
  firstPaymentDate: '',
  vscYears: '',
  vscMiles: '',
  currentMiles__vscMiles: '',
  originalWarrantyYears: '',
  originalWarrantyMiles: '',
  lienholderName: '',
  maxMarkup: '',
  moneyDown: '',
  // Default boolean fields to 'false' instead of empty string
  isBuyerNotLessee: 'false',
  isTitleOnly: 'false',
  isLesseeOnlyOrDoubleTax: 'false',
  isWalkInRequired: 'false',
  isDirectPayDeal: 'false',
  isRegTransferEligible: 'false',
};

const AiCallModal = ({ isOpen, onClose, dealId, calledNumber }: AiCallModalProps) => {
  const [callOutWithAi, { loading: isStartingCall }] = useCallOutWithAiMutation();
  const [aiCallPayload, { loading: isLoadingPayload, data: payloadData }] =
    useAiCallPayloadLazyQuery();

  useEffect(() => {
    if (isOpen && dealId) {
      aiCallPayload({
        variables: { dealId },
        onError: () => {
          toast.error('Failed to load AI call data');
        },
      });
    }
  }, [isOpen, dealId, aiCallPayload]);

  const handleSubmit = async (values: AiCallInputPayload) => {
    if (values.dealId !== dealId) {
      toast.error('Deal ID does not match');
      return;
    }

    try {
      const response = await callOutWithAi({
        variables: {
          payload: values,
          calledNumber,
        },
      });

      if (response.errors) {
        toast.error('Failed to initiate AI call');
      } else {
        toast.success('AI call initiated successfully');
        onClose();
      }
    } catch {
      toast.error('Failed to initiate AI call');
    }
  };

  const getInitialValues = (): AiCallInputPayload => {
    if (payloadData?.aiCallPayload) {
      return {
        dealId: payloadData.aiCallPayload.dealId || '',
        dealSource: payloadData.aiCallPayload.dealSource || '',
        driverName: payloadData.aiCallPayload.driverName || '',
        repName: payloadData.aiCallPayload.repName || '',
        vehicleYearMakeModel: payloadData.aiCallPayload.vehicleYearMakeModel || '',
        vehicleMileage: payloadData.aiCallPayload.vehicleMileage || '',
        payoffAmount: payloadData.aiCallPayload.payoffAmount || '',
        bankName: payloadData.aiCallPayload.bankName || '',
        termInMonths: payloadData.aiCallPayload.termInMonths || '',
        sellRate: payloadData.aiCallPayload.sellRate || '',
        buyRate: payloadData.aiCallPayload.buyRate || '',
        monthlyPaymentWithVSCGAP: payloadData.aiCallPayload.monthlyPaymentWithVSCGAP || '',
        monthlyPaymentWithVSC: payloadData.aiCallPayload.monthlyPaymentWithVSC || '',
        monthlyPaymentWithGap: payloadData.aiCallPayload.monthlyPaymentWithGap || '',
        monthlyPaymentWithNoCoverage: payloadData.aiCallPayload.monthlyPaymentWithNoCoverage || '',
        firstPaymentDate: payloadData.aiCallPayload.firstPaymentDate || '',
        vscYears: payloadData.aiCallPayload.vscYears || '',
        vscMiles: payloadData.aiCallPayload.vscMiles || '',
        currentMiles__vscMiles: payloadData.aiCallPayload.currentMiles__vscMiles || '',
        originalWarrantyYears: payloadData.aiCallPayload.originalWarrantyYears || '',
        originalWarrantyMiles: payloadData.aiCallPayload.originalWarrantyMiles || '',
        lienholderName: payloadData.aiCallPayload.lienholderName || '',
        maxMarkup: payloadData.aiCallPayload.maxMarkup || '',
        moneyDown: payloadData.aiCallPayload.moneyDown || '',
        // Default boolean fields to 'false' when empty or undefined from API
        isBuyerNotLessee: payloadData.aiCallPayload.isBuyerNotLessee || 'false',
        isTitleOnly: payloadData.aiCallPayload.isTitleOnly || 'false',
        isLesseeOnlyOrDoubleTax: payloadData.aiCallPayload.isLesseeOnlyOrDoubleTax || 'false',
        isWalkInRequired: payloadData.aiCallPayload.isWalkInRequired || 'false',
        isDirectPayDeal: payloadData.aiCallPayload.isDirectPayDeal || 'false',
        isRegTransferEligible: payloadData.aiCallPayload.isRegTransferEligible || 'false',
      };
    }
    return initialValues;
  };

  const renderInput = (name: keyof AiCallInputPayload, label: string, type = 'text') => (
    <Field name={name} key={name}>
      {({ field, meta }: FieldProps) => (
        <FormControl isRequired isInvalid={!!meta.error && meta.touched}>
          <FormLabel fontSize="sm">{label}</FormLabel>
          <Input {...field} size="sm" type={type} />
          <FormErrorMessage>{meta.error}</FormErrorMessage>
        </FormControl>
      )}
    </Field>
  );

  const renderBoolean = (name: keyof AiCallInputPayload, label: string) => (
    <Field name={name} key={name}>
      {({ field, form, meta }: FieldProps) => (
        <FormControl isInvalid={!!meta.error && meta.touched}>
          <Checkbox
            isChecked={field.value === 'true'}
            onChange={(e) => form.setFieldValue(name, e.target.checked ? 'true' : 'false')}
          >
            {label}
          </Checkbox>
          <FormErrorMessage>{meta.error}</FormErrorMessage>
        </FormControl>
      )}
    </Field>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>AI Call Configuration</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoadingPayload ? (
            <VStack spacing={4} py={8}>
              <Spinner size="lg" />
              <Text>Loading AI call data...</Text>
            </VStack>
          ) : (
            <Formik
              initialValues={getInitialValues()}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ isValid }) => (
                <Form>
                  <VStack spacing={6} align="stretch">
                    <Alert status="info">
                      <AlertIcon />
                      Review and edit the AI call parameters below, then click "Start Call" to
                      begin.
                    </Alert>

                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      {renderInput('dealId', 'Deal ID')}
                      {renderInput('dealSource', 'Deal Source')}
                      {renderInput('driverName', 'Driver Name')}
                      {renderInput('repName', 'Rep Name')}
                      {renderInput('vehicleYearMakeModel', 'Vehicle Year/Make/Model')}
                      {renderInput('vehicleMileage', 'Vehicle Mileage')}
                      {renderInput('payoffAmount', 'Payoff Amount')}
                      {renderInput('bankName', 'Bank Name')}
                      {renderInput('termInMonths', 'Term (Months)')}
                      {renderInput('sellRate', 'Sell Rate')}
                      {renderInput('buyRate', 'Buy Rate')}
                      {renderInput('monthlyPaymentWithVSCGAP', 'Monthly Payment (VSC+GAP)')}
                      {renderInput('monthlyPaymentWithVSC', 'Monthly Payment (VSC Only)')}
                      {renderInput('monthlyPaymentWithGap', 'Monthly Payment (GAP Only)')}
                      {renderInput('monthlyPaymentWithNoCoverage', 'Monthly Payment (No Coverage)')}
                      {renderInput('firstPaymentDate', 'First Payment Date')}
                      {renderInput('vscYears', 'VSC Years')}
                      {renderInput('vscMiles', 'VSC Miles')}
                      {renderInput('currentMiles__vscMiles', 'Current Miles / VSC Miles')}
                      {renderInput('originalWarrantyYears', 'Original Warranty Years')}
                      {renderInput('originalWarrantyMiles', 'Original Warranty Miles')}
                      {renderInput('lienholderName', 'Lienholder Name')}
                      {renderInput('maxMarkup', 'Max Markup')}
                      {renderInput('moneyDown', 'Money Down')}
                    </SimpleGrid>

                    <VStack spacing={3} align="start">
                      <Text fontWeight="semibold">Deal Characteristics:</Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                        {renderBoolean('isBuyerNotLessee', 'Is Buyer Not Lessee')}
                        {renderBoolean('isTitleOnly', 'Is Title Only')}
                        {renderBoolean('isLesseeOnlyOrDoubleTax', 'Is Lessee Only Or Double Tax')}
                        {renderBoolean('isWalkInRequired', 'Is Walk-In Required')}
                        {renderBoolean('isDirectPayDeal', 'Is Direct Pay Deal')}
                        {renderBoolean(
                          'isRegTransferEligible',
                          'Is Registration Transfer Eligible',
                        )}
                      </SimpleGrid>
                    </VStack>

                    <HStack spacing={3} justify="flex-end">
                      <Button variant="ghost" onClick={onClose} disabled={isStartingCall}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        colorScheme="blue"
                        isLoading={isStartingCall}
                        loadingText="Starting Call..."
                        disabled={!isValid}
                      >
                        Start Call
                      </Button>
                    </HStack>
                  </VStack>
                </Form>
              )}
            </Formik>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AiCallModal;
